<script setup lang="ts">
/**
 * A turn's plain changes as one quiet receipt under its reply ("✓ 3 changes ›"): tapping opens them in the
 * workspace pane (ChangesViewer), where each can be opened or undone, or all of them at once. Records the
 * creator has to review (drafts, invoices, contract reviews) stay in the conversation as cards; this is for the rest.
 * Outside a conversation (no workspace: the call screen) it's the same receipt, just not a button.
 */
import { computed } from 'vue'
import { artifactKey, changesArtifact } from '../artifacts'
import { useWorkspace } from '../workspace'
import { vPress } from '@/directives/press'

const props = defineProps<{ changesetId: string; count: number; undone: number; bubble?: boolean }>()

const ws = useWorkspace()
const target = computed(() => changesArtifact(props.changesetId, props.count))
const open = computed(() => ws?.presented && ws.current && artifactKey(ws.current) === artifactKey(target.value))
const label = computed(() => {
    const all = props.undone >= props.count
    const n = props.count.toLocaleString('en-US')
    if (all) return `${n} change${props.count === 1 ? '' : 's'} undone`
    return `${n} change${props.count === 1 ? '' : 's'}${props.undone ? ` · ${props.undone.toLocaleString('en-US')} undone` : ''}`
})
</script>

<template>
    <component
        :is="ws ? 'button' : 'span'"
        v-press="{ variant: 'pill', disabled: !ws }"
        :type="ws ? 'button' : undefined"
        class="changes-pill"
        :class="{ 'is-open': open, 'is-bubble': bubble, 'is-undone': undone >= count }"
        data-testid="changes-pill"
        :aria-label="ws ? `${label}: review or undo` : undefined"
        @click="ws?.open(target)"
    >
        <svg v-if="undone < count" class="tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M4.2 8.4l2.4 2.3 5.2-5.4" /></svg>
        <svg v-else class="tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M5 6.5h5.2a2.8 2.8 0 010 5.6H7M5 6.5L7 4.5M5 6.5l2 2" /></svg>
        <span>{{ label }}</span>
        <svg v-if="ws" class="go" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
    </component>
</template>

<style scoped>
.changes-pill {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 6px;
    height: 28px;
    padding: 0 10px 0 9px;
    border-radius: 999px;
    border: 1px solid var(--agent-card-border);
    background: var(--agent-card);
    font-size: 12.5px;
    color: var(--agent-text-2);
    transition: background-color 150ms ease, border-color 150ms ease;
}
button.changes-pill { cursor: pointer; }
@media (hover: hover) and (pointer: fine) {
    .changes-pill:hover { background: var(--agent-hover); }
}
.changes-pill:focus-visible { outline: 2px solid var(--agent-accent); outline-offset: 2px; }
.changes-pill.is-open { border-color: var(--agent-text-3, rgb(15 15 15 / 0.28)); }
.changes-pill.is-undone { color: var(--agent-text-3); }
.changes-pill.is-bubble { height: 32px; font-size: 13px; }
svg { flex-shrink: 0; fill: none; stroke: currentColor; stroke-linecap: round; stroke-linejoin: round; }
.tick { width: 14px; height: 14px; stroke-width: 1.8; color: #10b981; }
.is-undone .tick { color: var(--agent-text-3); }
.go { width: 11px; height: 11px; stroke-width: 1.8; color: var(--agent-text-4); }
</style>
